import {Skeleton, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {
	AuthorizationsEnum,
	ExamBookingEnum,
	GuidesEnum,
	HealthBookingEnum,
	OdontoBookingEnum,
	QueryParamsForTaggingEnum,
} from 'presentation/constants';
import {useBeneficiaryCard, usePersonalData} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {useGetHealthCardQuery} from 'presentation/pages/private/health-card/queries/useGetHealthCardQuery';

import {FlowEnum} from 'presentation/pages/private/booking/constants';

import {useAccountInformation, useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText, getUserInfoFromToken} from 'presentation/utils';
import {useNavigate} from 'react-router-dom';

import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {getStepPosition} from 'presentation/modules/booking/shared/utils/getStepPosition';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {FlowType} from 'presentation/pages/private/store/locationStore/constants';
import {useGetRecentLocation} from '../../hooks/useRecenteLocation';
import {StyledBox, StyledStack, StyledTypography} from './styles';
import {RecentLocationCardProps} from './types';
import {getBookingTypeAndOrigin} from 'presentation/modules/booking/shared/utils/getBookingTypeAndOrigin';

const {selectedRecent} = translations['pt-br'].commons;

export const RecentLocationCard = ({flowType}: RecentLocationCardProps) => {
	const theme = useTheme();
	const {currentLocation} = useAccountInformation();
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);
	const {currentEmail, currentPhone} = usePersonalData();
	const navigate = useNavigate();
	const setBookingData = useBookingStore(state => state.setBookingData);
	const isExamFlow = flowType === FlowEnum.EXAM;
	const isOdontoFlow = flowType === FlowEnum.ODONTO;
	const bookingFlowPath = () => {
		if (isExamFlow) {
			return ExamBookingEnum.EXAM_BOOKING_FLOW;
		}

		if (isOdontoFlow) {
			return OdontoBookingEnum.ODONTO_BOOKING_FLOW;
		}

		return HealthBookingEnum.HEALTH_BOOKING_FLOW;
	};
	const isGuideFlow = flowType === FlowEnum.HEALTH_GUIDE || flowType === FlowEnum.ODONTO_GUIDE;
	const cdPessoaC = getUserInfoFromToken().cdPessoa;

	const {beneficiaryCardPlanData} = useBeneficiaryCard();
	const {data: healthCard, isLoading: healthCardIsLoading} = useGetHealthCardQuery();
	const cdPlanoANSOdonto =
		healthCard?.registroPlanoAnsOdonto?.trim() || healthCard?.registroPlanoAns;
	const nuRegistroPlano = beneficiaryCardPlanData?.nuRegistroPlano;

	const setLocation = useLocationStore(state => state.setLocation);

	const {SPECIALITY_OR_EXAM_STEP} = getStepPosition({flowType, isManualLocation: true});

	const {data: localitiesList, isFetching} = useGetRecentLocation({
		flowType,
		cdUf: currentLocation.state.cdUf,
		cdPlanoANSOdonto,
		nuRegistroPlano,
	});

	const {setSearchParams} = useQueryParams();

	const isCurrentLocalityInLocalitiesList = localitiesList?.some(locality => {
		if ('nmLocal' in locality) {
			return locality?.nmLocal?.toLowerCase() === currentLocation?.city?.toLowerCase();
		}

		if ('nmCidade' in locality) {
			return locality?.nmCidade?.toLowerCase() === currentLocation?.city?.toLowerCase();
		}
		return false;
	});

	if (isFetching || healthCardIsLoading) {
		return (
			<Skeleton variant="rectangular" height={120} width="100%" sx={{borderRadius: '8px'}} />
		);
	}

	if (!isCurrentLocalityInLocalitiesList) return null;

	const cityName = currentLocation.city;
	const ufName = currentLocation.state.nmUf;
	const {cdUf} = currentLocation.state;

	const goToGuideFlow = () => {
		setLocation(
			cdPessoaC,
			flowType === FlowEnum.HEALTH_GUIDE ? FlowType.HEALTH_GUIDE : FlowType.ODONTO_GUIDE,
			{
				state: {
					cdUf,
					nmUf: ufName,
				},
				city: cityName,
			},
		);

		navigate(
			flowType === FlowEnum.ODONTO_GUIDE
				? GuidesEnum.SERVICE_NETWORK_ODONTO_FLOW_DISTRICT
				: GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_DISTRICT,
		);
	};

	const goToAuthorizationFlow = () => {
		setLocation(cdPessoaC, FlowType.AUTHORIZATION, {
			state: {
				cdUf,
				nmUf: ufName,
			},
			city: cityName,
		});

		navigate(AuthorizationsEnum.REQUEST);
	};

	const handleClick = () => {
		if (isGuideFlow) {
			goToGuideFlow();
			return;
		}

		if (flowType === FlowEnum.AUTHORIZATIONS) {
			goToAuthorizationFlow();
			return;
		}

		const {bookingType, bookingOrigin} = getBookingTypeAndOrigin(flowType);

		setBookingData(prev => ({
			...prev,
			email: currentEmail,
			phone: currentPhone,
			ufCode: currentLocation.state.cdUf,
			ufName,
			cityName,
			typeLocalName: currentLocation.typeLocalName,
			reference: currentLocation.reference,
			bookingType,
			bookingOrigin,
		}));

		setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);

		navigate(bookingFlowPath(), {
			replace: true,
		});
	};

	return (
		<StyledBox
			onClick={() => {
				handleClick();

				setSearchParams(prev => {
					prev.set(QueryParamsForTaggingEnum.GEOLOCATION, 'false');
					return prev;
				});
			}}
			data-tag-id={AppointmentBookingEnum.CLICK_LOCALIZACAO_SELECIONADA_RECENTEMENTE}>
			<Stack sx={{p: 2}} direction="row" alignItems="center" justifyContent="space-between">
				<Stack>
					<Typography color="text.primary">{formatText(cityName)}</Typography>
					<Typography variant="body2" color="text.secondary">
						{formatText(ufName)}
					</Typography>
				</Stack>
				<Icon name={'arrow-right-filled'} size={20} color={theme.palette.primary.main} />
			</Stack>

			<StyledStack>
				<StyledTypography variant="body2" color="text.secondary">
					{selectedRecent}
				</StyledTypography>
			</StyledStack>
		</StyledBox>
	);
};
